import React, { useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";

/**
 * Enhanced ParameterDefinitionEditor that allows:
 *  - Adding a new parameter
 *  - Removing existing parameters
 *  - Reordering parameters up/down
 *  - Editing name, type, default value, and choices
 */
export default function ParameterDefinitionEditor({ paramDefinitions, onUpdate }) {
  const [localDefs, setLocalDefs] = useState(() => {
    // Convert paramDefinitions into local editable objects
    return paramDefinitions.map((def) => {
      // Pre-fill a rawChoices string if there's an array
      let rawChoices = "";
      if (def.choices && Array.isArray(def.choices)) {
        rawChoices = def.choices.join(", ");
      }
      return {
        ...def,
        rawChoices
      };
    });
  });

  // List of possible types (including custom "Builds" and "Device")
  const typeOptions = [
    { label: "StringParameterDefinition", value: "StringParameterDefinition" },
    { label: "ChoiceParameterDefinition", value: "ChoiceParameterDefinition" },
    { label: "CascadeChoiceParameter", value: "CascadeChoiceParameter" },
    { label: "BooleanParameterDefinition", value: "BooleanParameterDefinition" },
    { label: "NumberParameterDefinition", value: "NumberParameterDefinition" },
    { label: "Builds (custom)", value: "Builds" },
    { label: "Device (custom)", value: "Device" },
  ];

  // --- HANDLERS FOR CHANGING FIELDS INSIDE localDefs --- //

  const handleNameChange = (index, newName) => {
    setLocalDefs((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], name: newName };
      return copy;
    });
  };

  const handleTypeChange = (index, newType) => {
    setLocalDefs((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], type: newType };
      return copy;
    });
  };

  const handleDefaultChange = (index, newDefault) => {
    setLocalDefs((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        defaultParameterValue: {
          ...copy[index].defaultParameterValue,
          value: newDefault
        }
      };
      return copy;
    });
  };

  const handleRawChoicesChange = (index, newRaw) => {
    setLocalDefs((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        rawChoices: newRaw
      };
      return copy;
    });
  };

  // --- ADD / REMOVE / REORDER --- //

  const handleAddParameter = () => {
    // Add a new param with some default structure
    const newParam = {
      name: `NewParam_${Date.now()}`,
      type: "StringParameterDefinition",
      defaultParameterValue: { value: "" },
      rawChoices: "",
      // Or anything else your code expects
    };

    setLocalDefs((prev) => [...prev, newParam]);
  };

  const handleRemoveParameter = (index) => {
    setLocalDefs((prev) => {
      const copy = [...prev];
      copy.splice(index, 1);
      return copy;
    });
  };

  const moveParamUp = (index) => {
    if (index <= 0) return; // can't move up
    setLocalDefs((prev) => {
      const copy = [...prev];
      const temp = copy[index - 1];
      copy[index - 1] = copy[index];
      copy[index] = temp;
      return copy;
    });
  };

  const moveParamDown = (index) => {
    if (index >= localDefs.length - 1) return; // can't move down
    setLocalDefs((prev) => {
      const copy = [...prev];
      const temp = copy[index + 1];
      copy[index + 1] = copy[index];
      copy[index] = temp;
      return copy;
    });
  };

  // --- SAVE CHANGES --- //
  const handleSaveChanges = () => {
    // Convert rawChoices -> choices array for each param as needed
    const finalDefs = localDefs.map((def) => {
      let newChoices = def.choices;
      if (def.type === "ChoiceParameterDefinition" || def.type === "CascadeChoiceParameter") {
        newChoices = def.rawChoices
          .split(",")
          .map((c) => c.trim())
          .filter((c) => c !== "");
      }
      return {
        ...def,
        choices: newChoices
      };
    });

    onUpdate(finalDefs);
  };

  return (
    <div style={{ marginBottom: "1rem" }}>
      <h4>Parameter Definitions Editor</h4>
      <Button
        label="Add New Parameter"
        onClick={handleAddParameter}
        style={{ marginBottom: "1rem" }}
      />
      {localDefs.length === 0 && <p>No parameters yet.</p>}

      {localDefs.map((def, idx) => {
        const {
          name,
          type,
          defaultParameterValue,
          rawChoices
        } = def;
        const defaultVal = defaultParameterValue?.value || "";

        return (
          <div
            key={idx}
            style={{
              border: "1px solid #ccc",
              padding: "1rem",
              marginBottom: "0.75rem"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <p>
                <strong>Parameter #{idx + 1}</strong>
              </p>
              <div>
                <Button
                  icon="pi pi-arrow-up"
                  className="p-button-text p-button-sm"
                  onClick={() => moveParamUp(idx)}
                />
                <Button
                  icon="pi pi-arrow-down"
                  className="p-button-text p-button-sm"
                  onClick={() => moveParamDown(idx)}
                />
                <Button
                  icon="pi pi-trash"
                  className="p-button-danger p-button-sm p-button-text"
                  onClick={() => handleRemoveParameter(idx)}
                />
              </div>
            </div>

            {/* Name */}
            <div style={{ marginBottom: "0.5rem" }}>
              <label style={{ fontWeight: "bold", width: "80px", display: "inline-block" }}>
                Name:
              </label>
              <InputText
                value={name}
                onChange={(e) => handleNameChange(idx, e.target.value)}
                style={{ marginLeft: "0.5rem" }}
              />
            </div>

            {/* Type */}
            <div style={{ marginBottom: "0.5rem" }}>
              <label style={{ fontWeight: "bold", width: "80px", display: "inline-block" }}>
                Type:
              </label>
              <Dropdown
                value={type}
                options={typeOptions}
                onChange={(e) => handleTypeChange(idx, e.value)}
                style={{ marginLeft: "0.5rem" }}
              />
            </div>

            {/* Default Value */}
            <div style={{ marginBottom: "0.5rem" }}>
              <label style={{ fontWeight: "bold", width: "80px", display: "inline-block" }}>
                Default:
              </label>
              <InputText
                value={defaultVal}
                onChange={(e) => handleDefaultChange(idx, e.target.value)}
                style={{ marginLeft: "0.5rem" }}
              />
            </div>

            {/* If it's a choice param, show text area for rawChoices */}
            {(type === "ChoiceParameterDefinition" || type === "CascadeChoiceParameter") && (
              <div style={{ marginBottom: "0.5rem" }}>
                <label style={{ fontWeight: "bold", width: "80px", display: "inline-block" }}>
                  Choices:
                </label>
                <br />
                <InputTextarea
                  rows={3}
                  cols={50}
                  style={{ marginTop: "0.25rem" }}
                  placeholder="Comma-separated values"
                  value={rawChoices}
                  onChange={(e) => handleRawChoicesChange(idx, e.target.value)}
                />
              </div>
            )}

            {/* You could add more type-specific fields if needed */}
          </div>
        );
      })}

      {/* Finalize changes */}
      <Button label="Save Changes" onClick={handleSaveChanges} />
    </div>
  );
}
